import type { Tool } from '../scene/reducer';
import {
  IconArrow,
  IconLock,
  IconDiamond,
  IconEllipse,
  IconEraser,
  IconHand,
  IconLine,
  IconPencil,
  IconRect,
  IconSelect,
  IconText,
} from './icons';

interface Props {
  readonly tool: Tool;
  readonly lockTool: boolean;
  readonly onSelectTool: (tool: Tool) => void;
  readonly onToggleLock: () => void;
}

interface Entry {
  readonly tool: Tool;
  readonly label: string;
  readonly key: string;
  readonly icon: () => JSX.Element;
}

export const TOOL_ENTRIES: readonly Entry[] = [
  { tool: 'select', label: 'Select and move', key: 'V', icon: IconSelect },
  { tool: 'rect', label: 'Rectangle', key: 'R', icon: IconRect },
  { tool: 'ellipse', label: 'Ellipse', key: 'O', icon: IconEllipse },
  { tool: 'diamond', label: 'Diamond', key: 'D', icon: IconDiamond },
  { tool: 'arrow', label: 'Arrow (connects shapes)', key: 'A', icon: IconArrow },
  { tool: 'line', label: 'Line', key: 'L', icon: IconLine },
  { tool: 'freedraw', label: 'Draw freehand', key: 'P', icon: IconPencil },
  { tool: 'text', label: 'Text', key: 'T', icon: IconText },
  { tool: 'eraser', label: 'Eraser', key: 'E', icon: IconEraser },
  { tool: 'pan', label: 'Pan the canvas', key: 'H', icon: IconHand },
];

export function Toolbar({ tool, lockTool, onSelectTool, onToggleLock }: Props) {
  return (
    <div className="tool-rail" role="toolbar" aria-label="Drawing tools" aria-orientation="vertical">
      {TOOL_ENTRIES.map((entry) => {
        const Icon = entry.icon;
        const active = tool === entry.tool;
        return (
          <button
            key={entry.tool}
            type="button"
            className="tool-btn"
            aria-pressed={active}
            aria-label={`${entry.label} (shortcut ${entry.key})`}
            title={`${entry.label} — ${entry.key}`}
            onClick={() => onSelectTool(entry.tool)}
          >
            <Icon />
            <kbd aria-hidden="true">{entry.key}</kbd>
          </button>
        );
      })}
      <hr />
      <button
        type="button"
        className="tool-btn"
        aria-pressed={lockTool}
        aria-label="Keep the current tool selected after drawing"
        title="Keep tool after drawing — Q"
        onClick={onToggleLock}
      >
        <IconLock open={!lockTool} />
      </button>
    </div>
  );
}
